import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ApiCallError } from '@/lib/api';
import { formatCprDisplay } from '@/lib/cpr';
import { useRevealReferralCpr, type ReferralCprSubject } from '@/hooks/useReferrals';

/**
 * "CPR registreret" badge for a CPR stored on a referral, with 'Vis CPR'
 * when the user may see it (C4). Revealing writes the CPR audit log
 * server-side; the value only lives in component state and is never logged.
 */
export function ReferralCprBadge({
  referralId,
  subject,
  canReveal,
}: {
  referralId: string;
  subject: ReferralCprSubject;
  canReveal: boolean;
}) {
  const { t } = useTranslation();
  const reveal = useRevealReferralCpr();
  const [cpr, setCpr] = useState<string | null>(null);

  const onToggle = async () => {
    if (cpr) {
      setCpr(null);
      return;
    }
    try {
      const r = await reveal.mutateAsync({
        referral_id: referralId,
        subject,
        source: 'referral-detail',
        purpose: 'referral_detail',
      });
      setCpr(r.cpr);
      toast.success(t('patients.toast_cpr_revealed'));
    } catch (err) {
      toast.error(err instanceof ApiCallError ? err.message : t('patients.errors.unknown'));
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex items-center rounded-full bg-ds-green-tint px-2 py-0.5 text-xs font-medium text-ds-green-ink">
        <CheckCircle2 className="mr-1 h-3 w-3" />
        {cpr ? <span className="font-mono">{formatCprDisplay(cpr)}</span> : t('referrals.cpr_present')}
      </div>
      {canReveal && (
        <button
          type="button"
          onClick={onToggle}
          disabled={reveal.isPending}
          className="inline-flex items-center gap-1 text-xs font-medium text-ds-teal hover:text-ds-teal disabled:opacity-50"
          data-testid={`referral-cpr-reveal-${subject}`}
        >
          {reveal.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : cpr ? (
            <EyeOff className="h-3 w-3" />
          ) : (
            <Eye className="h-3 w-3" />
          )}
          {cpr ? t('patients.cpr_hide') : t('patients.cpr_reveal')}
        </button>
      )}
    </div>
  );
}
